/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// INFINTOS newsletter confirmation page enhancement.
(() => {
  const successCopy = 'Vielen Dank, dass du dich für unsere Mailingliste angemeldet hast.';
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  function injectStyles() {
    if (document.getElementById('ifs-newsletter-confirmation-styles')) return;
    const style = document.createElement('style');
    style.id = 'ifs-newsletter-confirmation-styles';
    style.textContent = `
      .ifs-newsletter-confirmation-host{padding:0!important}
      .ifs-newsletter-confirmation{min-height:clamp(38rem,68vh,70rem);display:grid;place-items:center;padding:8rem 2.4rem;color:#0e100f;background:#fff}
      .ifs-newsletter-confirmation__inner{width:min(82rem,100%);margin:0 auto;text-align:center}
      .ifs-newsletter-confirmation__check{width:6.2rem;height:6.2rem;margin:0 auto 2.8rem;border-radius:50%;display:grid;place-items:center;background:#f3efe7;color:#a8844f;font-size:3rem;line-height:1}
      .ifs-newsletter-confirmation__kicker{margin:0 0 1.8rem;color:#a8844f;font-size:1.2rem;font-weight:700;line-height:1.2;letter-spacing:.19em;text-transform:uppercase}
      .ifs-newsletter-confirmation__title{margin:0;color:#0e100f;font-size:clamp(4.8rem,7vw,8.6rem);font-weight:500;line-height:.98;letter-spacing:-.055em}
      .ifs-newsletter-confirmation__lead{max-width:65rem;margin:2.8rem auto 3.4rem;color:#727771;font-size:2.1rem;line-height:1.6;letter-spacing:0}
      .ifs-newsletter-confirmation__button{display:inline-flex;align-items:center;justify-content:center;min-height:5.2rem;padding:1.7rem 3.1rem;border-radius:999px;background:#111;color:#fff!important;text-decoration:none;font-size:1.6rem;font-weight:700;line-height:1.2;letter-spacing:0}
      .ifs-newsletter-confirmation__button:hover{opacity:.88}
      .ifs-newsletter-confirmation__note{margin:2rem 0 0;color:#92968f;font-size:1.3rem;line-height:1.5;letter-spacing:0}
      @media(max-width:749px){.ifs-newsletter-confirmation{min-height:62rem;padding:6.5rem 2.2rem}.ifs-newsletter-confirmation__title{font-size:5.4rem}.ifs-newsletter-confirmation__lead{font-size:1.8rem}}
    `;
    document.head.appendChild(style);
  }

  function initInfintosNewsletterConfirmation() {
    const main = document.getElementById('MainContent');
    if (!main || main.dataset.ifsNewsletterEnhanced === 'true') return;
    const hasSuccessCopy = Array.from(main.querySelectorAll('h1,h2,h3,p,div')).some((el) => {
      if (el.children.length) return false;
      return normalize(el.textContent) === successCopy;
    });
    if (!hasSuccessCopy || !/abmelden/i.test(main.textContent || '')) return;

    injectStyles();
    main.dataset.ifsNewsletterEnhanced = 'true';
    main.classList.add('ifs-newsletter-confirmation-host');
    main.innerHTML = `
      <section class="ifs-newsletter-confirmation" data-ifs-newsletter-confirmation>
        <div class="ifs-newsletter-confirmation__inner">
          <div class="ifs-newsletter-confirmation__check" aria-hidden="true">✓</div>
          <p class="ifs-newsletter-confirmation__kicker">Willkommen bei INFINTOS</p>
          <h1 class="ifs-newsletter-confirmation__title">Du bist dabei.</h1>
          <p class="ifs-newsletter-confirmation__lead">Danke für deine Bestätigung. Ab jetzt erhältst du gelegentlich Post aus dem Schwarzwald – mit Neuigkeiten zu INFINTOS, unseren Produkten und Wissenswertem rund um Wasser.</p>
          <a class="ifs-newsletter-confirmation__button" href="/">Zurück zu INFINTOS</a>
          <p class="ifs-newsletter-confirmation__note">Du kannst dich jederzeit über den Link in unseren E-Mails wieder abmelden.</p>
        </div>
      </section>
    `;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfintosNewsletterConfirmation, { once: true });
  } else {
    initInfintosNewsletterConfirmation();
  }
})();